import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import { AttributeContainer, CrossContainer, DescriptionContainer, HeaderContainer, InputsContainer } from './styles';
import { CircularProgress, Divider, TextField } from '@mui/material';
import theme from '../../theme/theme';
import { Close } from '@mui/icons-material';
import { RequestModal } from '../../components/RequestModal';
import { InputModalProps } from './types';
import { putAdminReservations } from '../../services/reservations/putReservations';
import { useToasts } from "react-toast-notifications";
import { postRecoverPwd } from '../../services/user/postActions';
import { useNavigate } from "react-router-dom";

const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '70%',
    height: 'auto',
    minWidth: '250px',
    maxHeight: '700px',
    overflow: 'auto',
    borderRadius: '20px',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
};

const InputModal: React.FC<InputModalProps> = ({
    title,
    description,
    action,
    reservationID = 0,
    buttonMessage,
    onClose,
    update,
}) => {
    const navigate = useNavigate();
    const { addToast } = useToasts();

    // ------------------- JWT -------------------////////////////////////
    // let jwt = process.env.REACT_APP_JWT;
    const jwt = localStorage.getItem('jwt');

    // ------ Modal ------
    const [open, setOpen] = React.useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => {
        setOpen(false)
        setInput("");
        onClose();
        if (update !== undefined) {
            update();
        }
    };

    // ------ States ------
    const [input, setInput] = React.useState("");

    const [isPosted, setIsPosted] = React.useState(false);
    const [invalid, setInvalid] = React.useState(false);
    const [warningMessage, setWarningMessage] = React.useState("");

    const [loadingData, setLoadingData] = React.useState(false);
    const [loadingPostResource, setLoadingPostResource] = React.useState(false);

    // ------ Requests ------
    const modifyReservation = async (reservationId: number, status: string, message: string) => {
        status = status === "Approved" ? "Approved" : "Rejected";
        await putAdminReservations(jwt!, reservationId, status, message)
            .then((res: any) => {
                if (res && res.data) {
                    if (res.status > 299) {
                        setInvalid(true);
                        setWarningMessage("Verificar Recursos y Horarios");
                    } else {
                        setIsPosted(true);
                        if (status === "Rejected") {
                            setWarningMessage("Reservación RECHAZADA");
                        } else {
                            setWarningMessage("Reservación APROBADA");
                        }
                    }
                }
            })
            .catch((err: any) => {
                setInvalid(true);
                setWarningMessage("Verificar Recursos y Horarios");
            });
        setLoadingPostResource(false);
    }

    const recoverPWD = async (email: string) => {
        const emailValidation = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        if (!emailValidation) {
            addToast("El email no es válido", {
                appearance: "error",
                autoDismiss: true,
            });
            return;
        }

        setLoadingPostResource(true);
        await postRecoverPwd(email)
            .then((res: any) => {
                if (res.status < 299) {
                    addToast("Favor de verificar su correo", {
                        appearance: "success",
                        autoDismiss: true,
                    });
                    handleClose();
                } else {
                    addToast("Favor de verificar sus datos", {
                        appearance: "error",
                        autoDismiss: true,
                    });
                }
            })
            .catch((err: any) => {
                addToast("Favor de verificar sus datos", {
                    appearance: "error",
                    autoDismiss: true,
                });
            });

        setLoadingPostResource(false);
    }

    // ------ Main function ------
    const manageFunction = async () => {
        if (action === "ApproveReservation") {
            setLoadingPostResource(true);
            modifyReservation(reservationID, "Approved", input)
        } else if (action === "DenyReservation") {
            setLoadingPostResource(true);
            modifyReservation(reservationID, "Rejected", input)
        } else if (action === "RecoverPassword") {
            recoverPWD(input);
        }
    }

    React.useEffect(() => {
        handleOpen();
    }, []);

    return (
        <div>
            {
                isPosted ? (
                    <RequestModal
                        title='¡Modificación exitosa!'
                        description={warningMessage}
                        onClose={handleClose}
                    />
                ) : (
                    invalid &&
                    <RequestModal
                        title='Ocurrió un error'
                        description={warningMessage}
                        onClose={handleClose}
                    />
                )
            }
            <Modal
                open={open}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={style}>
                    {
                        loadingData &&
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <CircularProgress sx={{ color: theme.color.navy }} />
                        </div>
                    }
                    {loadingPostResource ? (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <CircularProgress sx={{ color: theme.color.navy }} />
                        </div>
                    ) : (
                        <>
                            <CrossContainer
                                onClick={handleClose}
                            >
                                <Close />
                            </CrossContainer>
                            <HeaderContainer>{title}</HeaderContainer>
                            <DescriptionContainer>{description}</DescriptionContainer>
                            <InputsContainer>
                                <AttributeContainer>
                                    <TextField
                                        label={action === "RecoverPassword" ? "Correo" : "Mensaje"}
                                        type="text"
                                        variant="outlined"
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        fullWidth
                                        // disabled={true}
                                        sx={{ margin: "15px 0", width: '100%' }}
                                        InputProps={{
                                            style: {
                                                borderRadius: "14px",
                                            },
                                        }}
                                    />
                                </AttributeContainer>
                            </InputsContainer>
                            <Divider />
                            <Button
                                variant="contained"
                                onClick={manageFunction}
                                sx={{
                                    width: "100%",
                                    marginTop: "20px",
                                    borderRadius: "15px",
                                    fontSize: "16px",
                                    backgroundColor: theme.color.navy,
                                    color: theme.color.white,
                                    textTransform: "none",
                                    "&:hover": {
                                        backgroundColor: theme.color.space,
                                    },
                                }}
                            >{buttonMessage}
                            </Button>
                        </>
                    )}
                </Box>
            </Modal>
        </div>
    );
}

export default InputModal;